import { useQuery } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plug, Plus } from 'lucide-react';
import { fetchMyChargers, type MyCharger } from '@/lib/api';
import { StatusPill } from '@/components/primitives';
import { mediaForCharger } from '@/lib/chargerMedia';
import { viewForConnectorStatus } from '@/lib/connectorStatus';
import { isAwaitingFirstConnection } from '@/lib/homeChargerConnection';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './MyChargersPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Resident-managed chargers list — the driver-tier landing for the
 * "My Chargers" flow. Filters Charger.created_by_user_id === me on
 * the server and returns a tight projection (no admin internals).
 *
 * Empty state is the most common state: most drivers will never
 * register a charger. The empty CTA leads straight into the add-
 * charger flow rather than dumping them into a generic "no results"
 * page so the row never feels like a dead-end.
 */
export function MyChargersPage({ onNavigate }: Props) {
  const { t } = useTranslation('myChargersManage');
  // The row pills show connector status, so the list tracks the charger
  // while open: live charger events (the owner hears their own chargers),
  // with the 10s poll as the fallback when the stream is not live.
  const refreshMs = useStreamAwareInterval(10_000);
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-chargers'],
    queryFn: fetchMyChargers,
    refetchInterval: refreshMs,
    refetchIntervalInBackground: false,
  });
  const chargers = data ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('list.title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('list.loading')
              : t('list.subtitle', { count: chargers.length })}
          </p>
        </div>
        {!isLoading && chargers.length > 0 && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => onNavigate('/my-chargers/add')}
            aria-label={t('list.addAria')}
          >
            <Plus size={16} />
          </button>
        )}
      </header>

      {isLoading && (
        <div className={styles.list}>
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('list.loadError')}
        </div>
      )}

      {!isLoading && !error && chargers.length === 0 && (
        <MyChargersEmpty onAdd={() => onNavigate('/my-chargers/add')} />
      )}

      {!isLoading && !error && chargers.length > 0 && (
        <div className={styles.list}>
          {chargers.map((charger) => (
            <ChargerCard
              key={charger._id}
              charger={charger}
              // MyChargerDetailPage at /my-chargers/:id is now live
              // (resident-managed Phase 3 + Phase 6 settings form).
              // The URL pattern requires the 24-hex ObjectId, not the
              // OCPP identity — we pass charger._id explicitly.
              onOpen={() => onNavigate(`/my-chargers/${charger._id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChargerCard({
  charger, onOpen,
}: { charger: MyCharger; onOpen: () => void }) {
  const { t } = useTranslation('myChargersManage');
  const { t: tHome } = useTranslation('myHome');
  const sharingLabel = charger.is_open_to_guests ? t('card.shared') : t('card.private');
  const connector = charger.connectors[0];
  // Canonical connector-status view (Offline wins), same mapping as the
  // public station rows and the My Home card.
  const statusView = viewForConnectorStatus(connector?.status, charger.is_online);
  // Register-first: never connected reads as waiting, not Offline.
  const awaiting = isAwaitingFirstConnection(charger);
  // Model-matched ambient loop; unmatched models keep the plug glyph.
  const media = mediaForCharger(charger);
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <button
      type="button"
      className={styles.card}
      onClick={onOpen}
    >
      <div className={styles.photo}>
        {charger.photo_url ? (
          // The img background load resolves over the gradient
          // fallback below — same pattern as FavoriteRow's photoUrl
          // path.
          <img
            src={charger.photo_url}
            alt=""
            className={styles.photoImg}
            loading="lazy"
          />
        ) : media ? (
          reduceMotion ? (
            <img src={media.poster} alt="" className={styles.photoImg} />
          ) : (
            <video
              className={styles.photoImg}
              src={media.loop}
              poster={media.poster}
              preload="metadata"
              autoPlay
              muted
              loop
              playsInline
            />
          )
        ) : (
          <div className={styles.photoFallback} aria-hidden>
            <NamedIcon
              name="plug"
              fallback={<Plug size={96} strokeWidth={1.25} />}
              size={96}
            />
          </div>
        )}
        <span className={styles.photoOverlay}>
          <StatusPill tone={awaiting ? 'info' : statusView.tone} variant="solid" size="sm">
            {awaiting ? tHome('connection.waitingPill') : statusView.label}
          </StatusPill>
        </span>
      </div>
      <div className={styles.cardBody}>
        <p className={styles.cardEyebrow}>{charger.location_name}</p>
        <p className={styles.cardTitle}>{charger.identity}</p>
        <p className={styles.cardMeta}>
          {charger.vendor} {charger.model}
          {connector ? ` · ${connector.type} · ${connector.max_power_kw} kW` : ''}
        </p>
        <div className={styles.cardPills}>
          <StatusPill
            tone={charger.is_open_to_guests ? 'accent' : 'mute'}
            variant="soft"
            size="sm"
          >
            {sharingLabel}
          </StatusPill>
          {charger.created_via === 'resident_self_service' && (
            <StatusPill tone="info" variant="soft" size="sm">
              {t('card.resident')}
            </StatusPill>
          )}
        </div>
      </div>
    </button>
  );
}

function MyChargersEmpty({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation('myChargersManage');
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <NamedIcon name="chargers-empty" fallback={<Plug size={40} />} size={40} />
      </div>
      <h2 className={styles.emptyTitle}>{t('empty.title')}</h2>
      <p className={styles.emptyDesc}>
        {t('empty.desc')}
      </p>
      <button type="button" className={styles.emptyCta} onClick={onAdd}>
        <Plus size={14} /> {t('empty.cta')}
      </button>
    </div>
  );
}
